// Widget Integration Page - Show customers how to embed the widget
// Supports multiple products per organization, each with unique project ID
import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Stack,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  CircularProgress,
  Collapse,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ContentCopy,
  Check,
  Code,
  Refresh,
  Visibility,
  Settings,
  Info,
  Inventory2,
  Security,
  PlayArrow,
  Close,
  People,
  Api,
  Help,
  BugReport,
  Lightbulb,
  SupportAgent,
  Screenshot,
  Terminal,
  Delete,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useToast } from '@/contexts/ToastContext';
import { organizationsService } from '@/services/collections/organizations';
import type { WidgetSettings } from '@/types';

export const WidgetIntegrationPage = () => {
  const { organization, project } = useOrganization();
  const { showSuccess, showError } = useToast();

  const [tab, setTab] = useState(0);
  const [frameworkTab, setFrameworkTab] = useState(0);
  const [docTab, setDocTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [widgetView, setWidgetView] = useState<'menu' | 'bug' | 'feature' | 'help'>('menu');

  // Screen capture and console log state for bug reports
  const [includeScreenshot, setIncludeScreenshot] = useState(false);
  const [includeConsoleLogs, setIncludeConsoleLogs] = useState(false);
  const [screenshotData, setScreenshotData] = useState<string | null>(null);
  const [consoleLogs, setConsoleLogs] = useState<Array<{ type: string; message: string; timestamp: string }>>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showConsolePreview, setShowConsolePreview] = useState(false);

  // Widget settings state
  const [settings, setSettings] = useState<WidgetSettings>(
    project?.widgetSettings || {
      enabled: true,
      position: 'bottom-right',
      primaryColor: '#2563eb',
      launcherIcon: 'help',
      showKnowledgeBase: true,
      showFeaturePortal: true,
      showContactForm: true,
      collectEmail: true,
      collectName: false,
      requireEmail: true,
      triggerDelay: 0,
      hideOnMobile: false,
      zIndex: 999999,
    }
  );

  const projectId = project?.id || 'YOUR_PROJECT_ID';
  const apiKey = project?.apiKey || 'YOUR_API_KEY';
  const projectName = project?.name || 'Your Product';
  const widgetUrl = 'https://cdn.supportspace.io/widget.js';

  // Generate embed code based on framework
  const getEmbedCode = (framework: string, includeUserIdentification = false) => {
    const userIdHtml = includeUserIdentification
      ? `\n  data-user-id="USER_ID_HERE"\n  data-user-email="USER_EMAIL_HERE"\n  data-user-name="USER_NAME_HERE"`
      : '';
    const userIdReact = includeUserIdentification
      ? `\n        userId={user?.id}  // Optional: Identify users\n        userEmail={user?.email}\n        userName={user?.name}`
      : '';

    switch (framework) {
      case 'html':
        return `<!-- SupportSpace Widget for: ${projectName} -->
<!-- Project ID: ${projectId} -->
<script
  src="${widgetUrl}"
  data-project-id="${projectId}"
  data-key="${apiKey}"
  data-position="${settings.position}"
  data-color="${settings.primaryColor}"${userIdHtml}
  async>
</script>`;

      case 'react':
        return `// SupportSpace Widget for: ${projectName}
// Project ID: ${projectId}
// Install: npm install @supportspace/react
import { SupportSpaceWidget } from '@supportspace/react';

function App() {
  return (
    <>
      {/* Your app content */}
      <SupportSpaceWidget
        projectId="${projectId}"
        apiKey="${apiKey}"
        position="${settings.position}"
        primaryColor="${settings.primaryColor}"${userIdReact}
      />
    </>
  );
}`;

      case 'vue':
        return `<!-- SupportSpace Widget for: ${projectName} -->
<!-- Project ID: ${projectId} -->
<!-- Install: npm install @supportspace/vue -->
<template>
  <div>
    <!-- Your app content -->
    <SupportSpaceWidget
      project-id="${projectId}"
      api-key="${apiKey}"
      position="${settings.position}"
      primary-color="${settings.primaryColor}"${includeUserIdentification ? '\n      :user-id="user?.id"\n      :user-email="user?.email"\n      :user-name="user?.name"' : ''}
    />
  </div>
</template>

<script setup>
import { SupportSpaceWidget } from '@supportspace/vue';
</script>`;

      case 'nextjs':
        return `// SupportSpace Widget for: ${projectName}
// Project ID: ${projectId}
// Install: npm install @supportspace/react
'use client';
import { SupportSpaceWidget } from '@supportspace/react';

export default function RootLayout({ children }) {
  return (
    <html>
      <body>
        {children}
        <SupportSpaceWidget
          projectId="${projectId}"
          apiKey="${apiKey}"
          position="${settings.position}"
          primaryColor="${settings.primaryColor}"${userIdReact}
        />
      </body>
    </html>
  );
}`;

      case 'gtm':
        return `<!-- SupportSpace Widget - Google Tag Manager -->
<!-- Project: ${projectName} | ID: ${projectId} -->
<script>
(function() {
  var script = document.createElement('script');
  script.src = '${widgetUrl}';
  script.setAttribute('data-project-id', '${projectId}');
  script.setAttribute('data-key', '${apiKey}');
  script.setAttribute('data-position', '${settings.position}');
  script.setAttribute('data-color', '${settings.primaryColor}');
  script.async = true;
  document.head.appendChild(script);
})();
</script>`;

      case 'programmatic':
        return `<!-- SupportSpace Widget - Programmatic Mode -->
<!-- Project: ${projectName} | ID: ${projectId} -->
<!-- The launcher button is hidden. Use JavaScript API to control. -->

<script
  src="${widgetUrl}"
  data-project-id="${projectId}"
  data-key="${apiKey}"
  data-position="${settings.position}"
  data-color="${settings.primaryColor}"
  data-hide-launcher="true"
  async>
</script>

<script>
// Wait for widget to load, then use the API
window.addEventListener('SupportSpaceReady', function() {
  // Widget is ready to use

  // Open the widget (shows the main menu)
  // window.SupportSpace.open();

  // Open directly to bug report form
  // window.SupportSpace.open({ view: 'bug' });

  // Open directly to feature request form
  // window.SupportSpace.open({ view: 'feature' });

  // Open directly to help/contact form
  // window.SupportSpace.open({ view: 'help' });

  // Close the widget
  // window.SupportSpace.close();

  // Toggle the widget open/closed
  // window.SupportSpace.toggle();
});

// Example: Attach to your own button
document.getElementById('my-help-button')?.addEventListener('click', function() {
  window.SupportSpace.open();
});

// Example: Open bug report from error boundary
function handleError(error) {
  window.SupportSpace.open({
    view: 'bug',
    prefill: {
      description: 'Error: ' + error.message
    }
  });
}
</script>`;

      default:
        return '';
    }
  };

  // Copy code to clipboard
  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    showSuccess('Code copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  // Save widget settings
  const handleSaveSettings = async () => {
    if (!organization || !project) return;

    setSaving(true);
    try {
      await organizationsService.updateProject(organization.id, project.id, {
        widgetSettings: settings,
      });
      showSuccess('Widget settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      showError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Regenerate API key
  const handleRegenerateKey = async () => {
    if (!organization || !project) return;
    if (!window.confirm('Are you sure? This will invalidate the current API key.')) return;

    try {
      const newKey = `ss_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 15)}`;
      await organizationsService.updateProject(organization.id, project.id, {
        apiKey: newKey,
      });
      showSuccess('API key regenerated. Update your embed code.');
      window.location.reload();
    } catch (error) {
      showError('Failed to regenerate API key');
    }
  };

  // Capture screenshot using browser's screen capture API
  const handleCaptureScreenshot = async () => {
    setIsCapturing(true);
    try {
      // In a real widget, this would capture the actual page
      // For this preview, we'll simulate with a placeholder
      // Real implementation would use:
      // const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      // Then capture a frame from the stream

      // Simulate capture delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // For demo purposes, create a simple canvas with simulated content
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 600;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Draw a simulated screenshot
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(0, 0, 800, 600);
        ctx.fillStyle = '#1e293b';
        ctx.font = 'bold 24px Arial';
        ctx.fillText('Screenshot Preview', 280, 280);
        ctx.font = '14px Arial';
        ctx.fillStyle = '#64748b';
        ctx.fillText('Captured at ' + new Date().toLocaleTimeString(), 300, 320);
        ctx.fillText('Page: https://your-awesome-app.com/dashboard', 250, 350);
      }

      const dataUrl = canvas.toDataURL('image/png');
      setScreenshotData(dataUrl);
      setIncludeScreenshot(true);
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
    } finally {
      setIsCapturing(false);
    }
  };

  // Capture console logs - in real widget, this would collect from intercepted console
  const handleCaptureConsoleLogs = () => {
    // Simulate captured console logs
    const simulatedLogs = [
      { type: 'error', message: 'Uncaught TypeError: Cannot read property "map" of undefined at ProductList.tsx:45', timestamp: new Date(Date.now() - 30000).toISOString() },
      { type: 'warn', message: 'React Hook useEffect has a missing dependency: "userId"', timestamp: new Date(Date.now() - 60000).toISOString() },
      { type: 'error', message: 'Failed to fetch: GET https://api.example.com/products 500 (Internal Server Error)', timestamp: new Date(Date.now() - 120000).toISOString() },
      { type: 'log', message: 'User session started', timestamp: new Date(Date.now() - 180000).toISOString() },
    ];
    setConsoleLogs(simulatedLogs);
    setIncludeConsoleLogs(true);
    setShowConsolePreview(true);
  };

  // Clear captured data when switching views
  const resetCapturedData = () => {
    setScreenshotData(null);
    setConsoleLogs([]);
    setIncludeScreenshot(false);
    setIncludeConsoleLogs(false);
    setShowConsolePreview(false);
  };

  const frameworks = [
    { id: 'html', label: 'HTML/JS', icon: '🌐' },
    { id: 'react', label: 'React', icon: '⚛️' },
    { id: 'vue', label: 'Vue', icon: '💚' },
    { id: 'nextjs', label: 'Next.js', icon: '▲' },
    { id: 'gtm', label: 'GTM', icon: '📊' },
    { id: 'programmatic', label: 'Programmatic', icon: '⚙️' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Widget Integration
          </Typography>
          <Typography color="text.secondary">
            Add the SupportSpace widget to your website or app in just 2 minutes
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={() => setTestDialogOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          Test Widget
        </Button>
      </Box>

      {/* Current Project Card */}
      <Card sx={{ mb: 3, border: 2, borderColor: 'primary.main' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Inventory2 color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  CURRENT PROJECT
                </Typography>
              </Stack>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {projectName}
              </Typography>
              <Box
                sx={{
                  fontFamily: 'monospace',
                  bgcolor: 'primary.50',
                  p: 1.5,
                  borderRadius: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  border: 1,
                  borderColor: 'primary.200',
                }}
              >
                <Typography variant="caption" color="text.secondary">Project ID:</Typography>
                <Box component="code" sx={{ fontWeight: 'bold' }}>{projectId}</Box>
                <Tooltip title="Copy Project ID">
                  <IconButton size="small" onClick={() => handleCopy(projectId)}>
                    {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Each project has its own widget code, branding, and knowledge base articles.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Security color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  API KEY
                </Typography>
              </Stack>
              <Box
                sx={{
                  fontFamily: 'monospace',
                  bgcolor: 'grey.100',
                  p: 1.5,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: 'space-between',
                }}
              >
                <code>{apiKey}</code>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Copy API Key">
                    <IconButton size="small" onClick={() => handleCopy(apiKey)}>
                      {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Regenerate Key">
                    <IconButton size="small" color="warning" onClick={handleRegenerateKey}>
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Keep this key secret. It identifies your project for secure communication.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Multi-Product Info */}
      <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Have multiple products or websites?
        </Typography>
        <Typography variant="body2">
          Create a separate <strong>Project</strong> for each product to maintain distinct branding,
          knowledge bases, and support workflows. Each project gets its own unique Project ID for the widget.
          Switch projects using the dropdown in the sidebar.
        </Typography>
      </Alert>

      {/* Public Support Portal Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Public Support Portal
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share this URL with your users so they can access your knowledge base, submit tickets,
            and track feature requests directly.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ flex: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {`${window.location.origin}/p/${organization?.slug || 'your-org'}`}
            </Box>
            <Tooltip title="Copy Portal URL">
              <IconButton
                onClick={() => handleCopy(`${window.location.origin}/p/${organization?.slug || 'your-org'}`)}
              >
                {copied ? <Check /> : <ContentCopy />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Open Portal">
              <IconButton
                onClick={() => window.open(`/p/${organization?.slug || ''}`, '_blank')}
                disabled={!organization?.slug}
              >
                <Visibility />
              </IconButton>
            </Tooltip>
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Chip label="Knowledge Base" size="small" variant="outlined" />
            <Chip label="Submit Request" size="small" variant="outlined" />
            <Chip label="Feature Roadmap" size="small" variant="outlined" />
            <Chip label="White-labeled" size="small" color="primary" variant="outlined" />
          </Stack>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<Code />} label="Embed Code" iconPosition="start" />
        <Tab icon={<Settings />} label="Widget Settings" iconPosition="start" />
        <Tab icon={<Visibility />} label="Preview" iconPosition="start" />
      </Tabs>

      {/* Embed Code Tab */}
      {tab === 0 && (
        <Stack spacing={3}>
          <Alert severity="info">
            Choose your framework below and copy the code. The widget will appear as a floating
            button that opens a feedback form when clicked.
          </Alert>

          {/* Framework Tabs */}
          <Card>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={frameworkTab} onChange={(_, v) => setFrameworkTab(v)} variant="scrollable">
                {frameworks.map((fw) => (
                  <Tab
                    key={fw.id}
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{fw.icon}</span>
                        <span>{fw.label}</span>
                      </Stack>
                    }
                  />
                ))}
              </Tabs>
            </Box>

            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2">
                  {frameworks[frameworkTab].label} Integration
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={copied ? <Check /> : <ContentCopy />}
                  onClick={() => handleCopy(getEmbedCode(frameworks[frameworkTab].id))}
                >
                  {copied ? 'Copied!' : 'Copy Code'}
                </Button>
              </Box>
              <Box
                component="pre"
                sx={{
                  bgcolor: '#1e1e1e',
                  color: '#d4d4d4',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  m: 0,
                }}
              >
                <code>{getEmbedCode(frameworks[frameworkTab].id)}</code>
              </Box>
            </CardContent>
          </Card>

          {/* Quick Start Steps */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Start
              </Typography>
              <Stack spacing={2}>
                {[
                  { step: 1, title: 'Copy the code', desc: 'Select your framework above and copy the embed code' },
                  { step: 2, title: 'Add to your site', desc: 'Paste the code before the closing </body> tag or in your app component' },
                  { step: 3, title: 'Test it out', desc: 'Reload your page and click the widget button to test' },
                ].map((item) => (
                  <Box key={item.step} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Chip label={item.step} size="small" color="primary" />
                    <Box>
                      <Typography variant="subtitle2">{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}

      {/* Widget Settings Tab */}
      {tab === 1 && (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6">Appearance</Typography>

              <FormControl fullWidth>
                <InputLabel>Position</InputLabel>
                <Select
                  value={settings.position}
                  label="Position"
                  onChange={(e) => setSettings({ ...settings, position: e.target.value as any })}
                >
                  <MenuItem value="bottom-right">Bottom Right</MenuItem>
                  <MenuItem value="bottom-left">Bottom Left</MenuItem>
                  <MenuItem value="top-right">Top Right</MenuItem>
                  <MenuItem value="top-left">Top Left</MenuItem>
                </Select>
              </FormControl>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Primary Color
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      bgcolor: settings.primaryColor,
                      border: 1,
                      borderColor: 'divider',
                    }}
                  />
                  <TextField
                    value={settings.primaryColor}
                    onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                    size="small"
                    sx={{ width: 120 }}
                  />
                  <Stack direction="row" spacing={0.5}>
                    {['#2563eb', '#7c3aed', '#10b981', '#f59e0b', '#ef4444'].map((color) => (
                      <Box
                        key={color}
                        onClick={() => setSettings({ ...settings, primaryColor: color })}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 0.5,
                          bgcolor: color,
                          cursor: 'pointer',
                          border: settings.primaryColor === color ? 2 : 0,
                          borderColor: 'primary.main',
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>

              <FormControl fullWidth>
                <InputLabel>Launcher Icon</InputLabel>
                <Select
                  value={settings.launcherIcon}
                  label="Launcher Icon"
                  onChange={(e) => setSettings({ ...settings, launcherIcon: e.target.value as any })}
                >
                  <MenuItem value="help">Help (Question Mark)</MenuItem>
                  <MenuItem value="chat">Chat Bubble</MenuItem>
                  <MenuItem value="feedback">Feedback</MenuItem>
                </Select>
              </FormControl>

              <Divider />
              <Typography variant="h6">Features</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showKnowledgeBase}
                    onChange={(e) => setSettings({ ...settings, showKnowledgeBase: e.target.checked })}
                  />
                }
                label="Show Knowledge Base search"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showContactForm}
                    onChange={(e) => setSettings({ ...settings, showContactForm: e.target.checked })}
                  />
                }
                label="Show Contact/Support Form"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showFeaturePortal}
                    onChange={(e) => setSettings({ ...settings, showFeaturePortal: e.target.checked })}
                  />
                }
                label="Show Feature Request option"
              />

              <Divider />
              <Typography variant="h6">Form Settings</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.collectEmail}
                    onChange={(e) => setSettings({ ...settings, collectEmail: e.target.checked })}
                  />
                }
                label="Collect email address"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.requireEmail}
                    onChange={(e) => setSettings({ ...settings, requireEmail: e.target.checked })}
                    disabled={!settings.collectEmail}
                  />
                }
                label="Require email (mandatory)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.collectName}
                    onChange={(e) => setSettings({ ...settings, collectName: e.target.checked })}
                  />
                }
                label="Collect name"
              />

              <Divider />
              <Typography variant="h6">Display Options</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.hideOnMobile}
                    onChange={(e) => setSettings({ ...settings, hideOnMobile: e.target.checked })}
                  />
                }
                label="Hide widget on mobile devices"
              />

              <TextField
                label="Trigger Delay (ms)"
                type="number"
                value={settings.triggerDelay}
                onChange={(e) => setSettings({ ...settings, triggerDelay: parseInt(e.target.value) || 0 })}
                helperText="Delay before widget appears (0 = immediate)"
                size="small"
                sx={{ maxWidth: 200 }}
              />

              <Box sx={{ pt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Preview Tab */}
      {tab === 2 && (
        <Grid container spacing={3}>
          {/* Settings Info */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Widget Configuration Summary
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Review your widget settings and test the widget experience on a sample page.
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Appearance
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Position</Typography>
                        <Typography variant="body2" fontWeight="medium">{settings.position}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">Primary Color</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: settings.primaryColor }} />
                          <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace' }}>
                            {settings.primaryColor}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Launcher Icon</Typography>
                        <Typography variant="body2" fontWeight="medium">{settings.launcherIcon}</Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Features
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Knowledge Base</Typography>
                        <Chip label={settings.showKnowledgeBase ? 'Enabled' : 'Disabled'} size="small" color={settings.showKnowledgeBase ? 'success' : 'default'} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Contact Form</Typography>
                        <Chip label={settings.showContactForm ? 'Enabled' : 'Disabled'} size="small" color={settings.showContactForm ? 'success' : 'default'} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Feature Requests</Typography>
                        <Chip label={settings.showFeaturePortal ? 'Enabled' : 'Disabled'} size="small" color={settings.showFeaturePortal ? 'success' : 'default'} />
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Form Settings
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Collect Email</Typography>
                        <Chip label={settings.collectEmail ? 'Yes' : 'No'} size="small" color={settings.collectEmail ? 'success' : 'default'} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Require Email</Typography>
                        <Chip label={settings.requireEmail ? 'Yes' : 'No'} size="small" color={settings.requireEmail ? 'warning' : 'default'} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Collect Name</Typography>
                        <Chip label={settings.collectName ? 'Yes' : 'No'} size="small" color={settings.collectName ? 'success' : 'default'} />
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Display Options
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Hide on Mobile</Typography>
                        <Chip label={settings.hideOnMobile ? 'Yes' : 'No'} size="small" color={settings.hideOnMobile ? 'warning' : 'default'} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Trigger Delay</Typography>
                        <Typography variant="body2" fontWeight="medium">{settings.triggerDelay}ms</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Side Preview Panel - Similar to Branding Page */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    PREVIEW
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<PlayArrow />}
                    onClick={() => setTestDialogOpen(true)}
                  >
                    Test Widget
                  </Button>
                </Box>

                {/* Mini Widget Preview */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  {/* Widget Header Preview */}
                  <Box
                    sx={{
                      bgcolor: settings.primaryColor,
                      color: 'white',
                      p: 2,
                      borderRadius: 1,
                      mb: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        mx: 'auto',
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                      }}
                    >
                      {settings.launcherIcon === 'help' && '?'}
                      {settings.launcherIcon === 'chat' && '💬'}
                      {settings.launcherIcon === 'feedback' && '📝'}
                    </Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {projectName}
                    </Typography>
                  </Box>

                  {/* Widget Options Preview */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ height: 8, bgcolor: '#e2e8f0', borderRadius: 1, width: '100%' }} />
                    <Box sx={{ height: 8, bgcolor: '#e2e8f0', borderRadius: 1, width: '80%' }} />
                    <Box sx={{ height: 8, bgcolor: '#e2e8f0', borderRadius: 1, width: '60%' }} />
                  </Box>

                  {/* Footer */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', textAlign: 'center', mt: 2 }}
                  >
                    Powered by SupportSpace
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Documentation Section - Tabbed Interface */}
      <Card sx={{ mt: 4 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={docTab}
              onChange={(_, v) => setDocTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<Inventory2 />} label="Multi-Product" iconPosition="start" />
              <Tab icon={<People />} label="User ID" iconPosition="start" />
              <Tab icon={<Api />} label="API" iconPosition="start" />
              <Tab icon={<Help />} label="Troubleshooting" iconPosition="start" />
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            {/* Multi-Product Setup Tab */}
            {docTab === 0 && (
              <Stack spacing={2}>
                <Typography variant="h6">Multi-Product Setup</Typography>
                <Typography variant="body2">
                  SupportSpace supports multiple products within a single organization. Each <strong>Project</strong> represents
                  a product or website and includes:
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" variant="body2">Unique Project ID for widget identification</Typography>
                  <Typography component="li" variant="body2">Custom branding (colors, logo, name)</Typography>
                  <Typography component="li" variant="body2">Separate knowledge base articles</Typography>
                  <Typography component="li" variant="body2">Independent support ticket queue</Typography>
                  <Typography component="li" variant="body2">Distinct feature request board</Typography>
                </Box>
                <Alert severity="success" variant="outlined">
                  <strong>Tip:</strong> Create a new project from Settings → General for each product. Then switch projects
                  using the dropdown in the sidebar to get the correct embed code.
                </Alert>
              </Stack>
            )}

            {/* User Identification Tab */}
            {docTab === 1 && (
              <Stack spacing={2}>
                <Typography variant="h6">User Identification (Optional)</Typography>
                <Typography variant="body2">
                  Identify logged-in users to provide better support context. When users submit tickets,
                  their info is automatically attached.
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: '#1e1e1e',
                    color: '#d4d4d4',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.8rem',
                  }}
                >
                  {`<script
  src="${widgetUrl}"
  data-project-id="${projectId}"
  data-key="${apiKey}"
  data-user-id="user_123"       <!-- Your user's ID -->
  data-user-email="user@example.com"
  data-user-name="John Doe"
  async>
</script>`}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  All user identification fields are optional. Provide what you have available.
                </Typography>
              </Stack>
            )}

            {/* Custom Events & API Tab */}
            {docTab === 2 && (
              <Stack spacing={2}>
                <Typography variant="h6">Custom Events & API</Typography>
                <Typography variant="body2">
                  Programmatically control the widget using JavaScript. Use the <strong>Programmatic</strong> tab
                  in Embed Code to get the full hidden-launcher setup.
                </Typography>

                <Typography variant="subtitle2" sx={{ mt: 1 }}>Opening the Widget</Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: '#1e1e1e',
                    color: '#d4d4d4',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.8rem',
                  }}
                >
                  {`// Open the widget (main menu)
window.SupportSpace.open();

// Open directly to a specific view
window.SupportSpace.open({ view: 'bug' });     // Bug report
window.SupportSpace.open({ view: 'feature' }); // Feature request
window.SupportSpace.open({ view: 'help' });    // Help/contact form

// Open with pre-filled content
window.SupportSpace.open({
  view: 'bug',
  prefill: {
    subject: 'Error on checkout page',
    description: 'The payment failed with error...'
  }
});

// Close the widget
window.SupportSpace.close();

// Toggle open/closed
window.SupportSpace.toggle();`}
                </Box>

                <Typography variant="subtitle2" sx={{ mt: 1 }}>User Identification & Events</Typography>
                <Box
                  component="pre"
                  sx={{
                    bgcolor: '#1e1e1e',
                    color: '#d4d4d4',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.8rem',
                  }}
                >
                  {`// Identify user after login
window.SupportSpace.identify({
  id: 'user_123',
  email: 'user@example.com',
  name: 'John Doe'
});

// Track custom events
window.SupportSpace.track('purchase_completed', {
  orderId: '12345',
  amount: 99.99
});`}
                </Box>

                <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Tip:</strong> To hide the launcher button and trigger the widget from your own UI,
                    add <code>data-hide-launcher="true"</code> to the script tag. See the <strong>Programmatic</strong> tab
                    for the complete code.
                  </Typography>
                </Alert>
              </Stack>
            )}

            {/* Troubleshooting Tab */}
            {docTab === 3 && (
              <Stack spacing={2}>
                <Typography variant="h6">Troubleshooting</Typography>
                <Typography variant="body2" fontWeight="medium">Widget not appearing?</Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" variant="body2">Check that the script is loaded (no console errors)</Typography>
                  <Typography component="li" variant="body2">Verify the Project ID and API Key are correct</Typography>
                  <Typography component="li" variant="body2">Ensure the widget is enabled in settings</Typography>
                  <Typography component="li" variant="body2">Check for CSS z-index conflicts</Typography>
                </Box>
                <Typography variant="body2" fontWeight="medium" sx={{ mt: 1 }}>Still having issues?</Typography>
                <Typography variant="body2">
                  Contact support at <strong>support@supportspace.io</strong> with your Project ID and a link to your site.
                </Typography>
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Test Widget Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => {
          setTestDialogOpen(false);
          setWidgetOpen(false);
          setWidgetView('menu');
        }}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: {
            sx: { height: '80vh', maxHeight: 700 }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Test Your Widget</Typography>
            <Typography variant="body2" color="text.secondary">
              Click the widget button to interact with your configured widget
            </Typography>
          </Box>
          <IconButton onClick={() => {
            setTestDialogOpen(false);
            setWidgetOpen(false);
            setWidgetView('menu');
          }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden' }}>
          {/* Mock Website */}
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {/* Browser Chrome */}
            <Box
              sx={{
                bgcolor: 'grey.200',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: 1,
                borderColor: 'divider',
                flexShrink: 0,
              }}
            >
              <Stack direction="row" spacing={0.5}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f57' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#28ca41' }} />
              </Stack>
              <Box
                sx={{
                  flex: 1,
                  bgcolor: 'white',
                  borderRadius: 1,
                  px: 2,
                  py: 0.5,
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                https://your-awesome-app.com/dashboard
              </Box>
            </Box>

            {/* Mock Page Content - scrollable area */}
            <Box
              sx={{
                flex: 1,
                bgcolor: '#f8fafc',
                overflow: 'auto',
              }}
            >
              {/* Fake Website Content */}
              <Box sx={{ p: 4 }}>
                <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    >
                      A
                    </Box>
                    <Typography variant="h5" fontWeight="bold">Acme Dashboard</Typography>
                  </Box>

                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Welcome back, John!</Typography>
                      <Typography color="text.secondary">
                        This is a simulated website to test your SupportSpace widget configuration.
                        Click the floating button in the corner to see how your widget looks and feels.
                      </Typography>
                    </CardContent>
                  </Card>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">Total Users</Typography>
                          <Typography variant="h4">1,234</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">Revenue</Typography>
                          <Typography variant="h4">$45.2k</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">Active Sessions</Typography>
                          <Typography variant="h4">892</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
            {/* End of mock page content */}

            {/* Widget Panel (when open) - positioned relative to dialog, not mock page */}
            {widgetOpen && (
              <Box
                sx={{
                  position: 'absolute',
                  // For bottom positions, place above the launcher; for top, place below
                  ...(settings.position.includes('bottom')
                    ? { bottom: 90 }
                    : { top: 90 }),
                  [settings.position.includes('right') ? 'right' : 'left']: 24,
                  width: 360,
                  maxHeight: 'calc(100% - 120px)', // Leave room for launcher and margins
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: 6,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  zIndex: 10,
                }}
              >
                  {/* Widget Header */}
                  <Box
                    sx={{
                      bgcolor: settings.primaryColor,
                      color: 'white',
                      p: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {widgetView === 'menu' && `Hi there! How can we help?`}
                      {widgetView === 'bug' && 'Report a Bug'}
                      {widgetView === 'feature' && 'Request a Feature'}
                      {widgetView === 'help' && 'Get Help'}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {widgetView === 'menu' ? projectName : 'We typically reply within a few hours'}
                    </Typography>
                  </Box>

                  {/* Widget Content */}
                  <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {widgetView === 'menu' && (
                      <Stack spacing={1.5}>
                        {settings.showContactForm && (
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<BugReport />}
                            onClick={() => setWidgetView('bug')}
                            sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
                          >
                            Report a Bug
                          </Button>
                        )}
                        {settings.showFeaturePortal && (
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<Lightbulb />}
                            onClick={() => setWidgetView('feature')}
                            sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
                          >
                            Request a Feature
                          </Button>
                        )}
                        {settings.showKnowledgeBase && (
                          <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<SupportAgent />}
                            onClick={() => setWidgetView('help')}
                            sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
                          >
                            Get Help
                          </Button>
                        )}
                      </Stack>
                    )}

                    {widgetView !== 'menu' && (
                      <Stack spacing={2}>
                        <Button
                          size="small"
                          onClick={() => {
                            setWidgetView('menu');
                            resetCapturedData();
                          }}
                          sx={{ alignSelf: 'flex-start', mb: 1 }}
                        >
                          ← Back
                        </Button>

                        {settings.collectName && (
                          <TextField
                            label="Your Name"
                            size="small"
                            fullWidth
                            placeholder="John Doe"
                          />
                        )}
                        {settings.collectEmail && (
                          <TextField
                            label="Email"
                            size="small"
                            fullWidth
                            required={settings.requireEmail}
                            placeholder="john@example.com"
                          />
                        )}
                        <TextField
                          label={widgetView === 'bug' ? 'Describe the bug' : widgetView === 'feature' ? 'Describe your idea' : 'How can we help?'}
                          multiline
                          rows={3}
                          fullWidth
                          placeholder={
                            widgetView === 'bug'
                              ? "What happened? What did you expect to happen?"
                              : widgetView === 'feature'
                              ? "Describe the feature you'd like to see..."
                              : "What do you need help with?"
                          }
                        />

                        {/* Screen Capture and Console Log options - Bug Report only */}
                        {widgetView === 'bug' && (
                          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="medium" gutterBottom display="block">
                              Attach Diagnostics (Optional)
                            </Typography>

                            {/* Screenshot Capture */}
                            <Box sx={{ mb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={includeScreenshot}
                                      onChange={(e) => {
                                        setIncludeScreenshot(e.target.checked);
                                        if (!e.target.checked) setScreenshotData(null);
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      <Screenshot sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                      Screenshot
                                    </Typography>
                                  }
                                  sx={{ mr: 1 }}
                                />
                                {!screenshotData && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleCaptureScreenshot}
                                    disabled={isCapturing}
                                    startIcon={isCapturing ? <CircularProgress size={14} /> : <Screenshot />}
                                  >
                                    {isCapturing ? 'Capturing...' : 'Capture'}
                                  </Button>
                                )}
                              </Box>

                              {/* Screenshot Preview */}
                              {screenshotData && (
                                <Box sx={{ mt: 1, position: 'relative' }}>
                                  <Box
                                    component="img"
                                    src={screenshotData}
                                    alt="Screenshot preview"
                                    sx={{
                                      width: '100%',
                                      height: 80,
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                      border: 1,
                                      borderColor: 'divider',
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setScreenshotData(null);
                                      setIncludeScreenshot(false);
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 4,
                                      right: 4,
                                      bgcolor: 'rgba(0,0,0,0.5)',
                                      color: 'white',
                                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>

                            {/* Console Logs Capture */}
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={includeConsoleLogs}
                                      onChange={(e) => {
                                        setIncludeConsoleLogs(e.target.checked);
                                        if (!e.target.checked) {
                                          setConsoleLogs([]);
                                          setShowConsolePreview(false);
                                        }
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      <Terminal sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                      Console Logs
                                    </Typography>
                                  }
                                  sx={{ mr: 1 }}
                                />
                                {consoleLogs.length === 0 && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleCaptureConsoleLogs}
                                    startIcon={<Terminal />}
                                  >
                                    Capture
                                  </Button>
                                )}
                              </Box>

                              {/* Console Logs Preview */}
                              {consoleLogs.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => setShowConsolePreview(!showConsolePreview)}
                                  >
                                    <Typography variant="caption" color="text.secondary">
                                      {consoleLogs.filter(l => l.type === 'error').length} errors, {consoleLogs.filter(l => l.type === 'warn').length} warnings captured
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setConsoleLogs([]);
                                          setIncludeConsoleLogs(false);
                                          setShowConsolePreview(false);
                                        }}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                      {showConsolePreview ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                    </Box>
                                  </Box>

                                  <Collapse in={showConsolePreview}>
                                    <Box
                                      sx={{
                                        mt: 1,
                                        maxHeight: 100,
                                        overflow: 'auto',
                                        bgcolor: '#1e1e1e',
                                        borderRadius: 1,
                                        p: 1,
                                        fontFamily: 'monospace',
                                        fontSize: '0.7rem',
                                      }}
                                    >
                                      {consoleLogs.map((log, idx) => (
                                        <Box
                                          key={idx}
                                          sx={{
                                            color: log.type === 'error' ? '#f87171' : log.type === 'warn' ? '#fbbf24' : '#a3e635',
                                            mb: 0.5,
                                            wordBreak: 'break-all',
                                          }}
                                        >
                                          [{log.type}] {log.message.substring(0, 60)}...
                                        </Box>
                                      ))}
                                    </Box>
                                  </Collapse>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}

                        <Button
                          variant="contained"
                          fullWidth
                          sx={{ bgcolor: settings.primaryColor }}
                        >
                          Submit
                        </Button>
                        <Typography variant="caption" color="text.secondary" textAlign="center">
                          This is a preview. Submissions are not saved.
                        </Typography>
                      </Stack>
                    )}
                  </Box>

                  {/* Widget Footer */}
                  <Box
                    sx={{
                      borderTop: 1,
                      borderColor: 'divider',
                      p: 1,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Powered by SupportSpace
                    </Typography>
                  </Box>
                </Box>
              )}

            {/* Widget Launcher Button - positioned relative to dialog */}
            <Box
              onClick={() => setWidgetOpen(!widgetOpen)}
              sx={{
                position: 'absolute',
                [settings.position.includes('bottom') ? 'bottom' : 'top']: 24,
                [settings.position.includes('right') ? 'right' : 'left']: 24,
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: settings.primaryColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: 4,
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: 24,
                zIndex: 11,
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: 6,
                },
              }}
            >
              {widgetOpen ? '×' : (
                <>
                  {settings.launcherIcon === 'help' && '?'}
                  {settings.launcherIcon === 'chat' && '💬'}
                  {settings.launcherIcon === 'feedback' && '📝'}
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Alert severity="info" sx={{ flex: 1, mr: 2 }}>
            This is an interactive preview. Changes made here won't create actual tickets.
          </Alert>
          <Button
            variant="outlined"
            onClick={() => {
              setTestDialogOpen(false);
              setWidgetOpen(false);
              setWidgetView('menu');
            }}
          >
            Close Preview
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
